import type { ComputedShipStats } from '../types/ship.ts';
import type { FleetSlot } from '../types/fleet.ts';

export interface NpcFleetConfig {
  id: string;
  name: string;
  level: number;
  description: string;
  slots: FleetSlot[];
  designs: Record<string, ComputedShipStats>;
  reward: {
    metal: number;
    he3: number;
    credits: number;
    exp: number;
  };
}

export const NPC_FLEETS: Record<string, NpcFleetConfig> = {
  pirate_scout_1: {
    id: 'pirate_scout_1',
    name: 'Trinh Sát Hải Tặc Hư Không (Lv.1)',
    level: 1,
    description: 'Biệt đội tàu nhỏ trinh sát của cướp biển, hỏa lực yếu, trang bị thô sơ.',
    slots: [
      { slot_index: 2, design_id: 'npc_pirate_skiff', ship_count: 5 }, // Front-center
    ],
    designs: {
      npc_pirate_skiff: {
        blueprint_id: 'npc_pirate_skiff',
        name: 'Chiến Tàu Cướp Skiff',
        hull_id: 'corvette',
        total_hp: 350,
        total_shield: 30,
        total_armor: 10,
        total_damage: 35,
        damage_type: 'kinetic',
        range_phase: 2,
        accuracy: 75,
        evasion: 18,
        speed: 130,
        initiative: 110,
        used_capacity: 50,
        max_capacity: 70,
        used_energy: 40,
        max_energy: 60,
        unit_cost: { metal: 80, he3: 30, credits: 10 },
        unit_build_time_sec: 15,
        is_valid: true,
      },
    },
    reward: {
      metal: 250,
      he3: 150,
      credits: 80,
      exp: 100,
    },
  },

  pirate_raider_2: {
    id: 'pirate_raider_2',
    name: 'Tiểu Hạm Đội Cướp Bóc (Lv.2)',
    level: 2,
    description: 'Đội tuần tra trang bị khinh hạm bọc giáp và pháo laser xung kích.',
    slots: [
      { slot_index: 1, design_id: 'npc_pirate_skiff', ship_count: 6 },
      { slot_index: 2, design_id: 'npc_pirate_raider', ship_count: 4 },
      { slot_index: 3, design_id: 'npc_pirate_skiff', ship_count: 6 },
    ],
    designs: {
      npc_pirate_skiff: {
        blueprint_id: 'npc_pirate_skiff',
        name: 'Chiến Tàu Cướp Skiff',
        hull_id: 'corvette',
        total_hp: 350,
        total_shield: 30,
        total_armor: 10,
        total_damage: 35,
        damage_type: 'kinetic',
        range_phase: 2,
        accuracy: 75,
        evasion: 18,
        speed: 130,
        initiative: 110,
        used_capacity: 50,
        max_capacity: 70,
        used_energy: 40,
        max_energy: 60,
        unit_cost: { metal: 80, he3: 30, credits: 10 },
        unit_build_time_sec: 15,
        is_valid: true,
      },
      npc_pirate_raider: {
        blueprint_id: 'npc_pirate_raider',
        name: 'Khinh Hạm Đột Kích Raider',
        hull_id: 'frigate',
        total_hp: 850,
        total_shield: 100,
        total_armor: 30,
        total_damage: 90,
        damage_type: 'energy',
        range_phase: 2,
        accuracy: 82,
        evasion: 12,
        speed: 110,
        initiative: 125,
        used_capacity: 120,
        max_capacity: 150,
        used_energy: 110,
        max_energy: 140,
        unit_cost: { metal: 200, he3: 110, credits: 40 },
        unit_build_time_sec: 35,
        is_valid: true,
      },
    },
    reward: {
      metal: 650,
      he3: 450,
      credits: 220,
      exp: 300,
    },
  },

  void_dread_3: {
    id: 'void_dread_3',
    name: 'Hạm Đội Thiết Giáp Hư Không (Lv.3)',
    level: 3,
    description: 'Hạm đội hải tặc tinh nhuệ sở hữu khu trục hạm trang bị tên lửa tầm xa.',
    slots: [
      { slot_index: 2, design_id: 'npc_void_destroyer', ship_count: 3 },
      { slot_index: 4, design_id: 'npc_pirate_raider', ship_count: 5 },
      { slot_index: 6, design_id: 'npc_pirate_raider', ship_count: 5 },
      { slot_index: 8, design_id: 'npc_void_destroyer', ship_count: 2 },
    ],
    designs: {
      npc_pirate_raider: {
        blueprint_id: 'npc_pirate_raider',
        name: 'Khinh Hạm Đột Kích Raider',
        hull_id: 'frigate',
        total_hp: 850,
        total_shield: 100,
        total_armor: 30,
        total_damage: 90,
        damage_type: 'energy',
        range_phase: 2,
        accuracy: 82,
        evasion: 12,
        speed: 110,
        initiative: 125,
        used_capacity: 120,
        max_capacity: 150,
        used_energy: 110,
        max_energy: 140,
        unit_cost: { metal: 200, he3: 110, credits: 40 },
        unit_build_time_sec: 35,
        is_valid: true,
      },
      npc_void_destroyer: {
        blueprint_id: 'npc_void_destroyer',
        name: 'Khu Trục Hạm Thiết Giáp Hư Không',
        hull_id: 'destroyer',
        total_hp: 2100,
        total_shield: 220,
        total_armor: 55,
        total_damage: 180,
        damage_type: 'explosive',
        range_phase: 1,
        accuracy: 80,
        evasion: 7,
        speed: 90,
        initiative: 140,
        used_capacity: 260,
        max_capacity: 320,
        used_energy: 240,
        max_energy: 280,
        unit_cost: { metal: 550, he3: 320, credits: 120 },
        unit_build_time_sec: 80,
        is_valid: true,
      },
    },
    reward: {
      metal: 1800,
      he3: 1200,
      credits: 600,
      exp: 800,
    },
  },

  pirate_corps_4: {
    id: 'pirate_corps_4',
    name: 'Quân Đoàn Cướp Biển Titan (Lv.4)',
    level: 4,
    description: 'Chiến đoàn cướp biển bọc giáp titan, sử dụng tên lửa và pháo tự động xuyên khiên.',
    slots: [
      { slot_index: 1, design_id: 'npc_pirate_raider', ship_count: 8 },
      { slot_index: 2, design_id: 'npc_void_destroyer', ship_count: 5 },
      { slot_index: 3, design_id: 'npc_pirate_raider', ship_count: 8 },
      { slot_index: 5, design_id: 'npc_void_destroyer', ship_count: 4 },
    ],
    designs: {
      npc_pirate_raider: {
        blueprint_id: 'npc_pirate_raider',
        name: 'Khinh Hạm Đột Kích Raider',
        hull_id: 'frigate',
        total_hp: 950,
        total_shield: 120,
        total_armor: 35,
        total_damage: 95,
        damage_type: 'energy',
        range_phase: 2,
        accuracy: 82,
        evasion: 12,
        speed: 110,
        initiative: 125,
        used_capacity: 120,
        max_capacity: 150,
        used_energy: 110,
        max_energy: 140,
        unit_cost: { metal: 200, he3: 110, credits: 40 },
        unit_build_time_sec: 35,
        is_valid: true,
      },
      npc_void_destroyer: {
        blueprint_id: 'npc_void_destroyer',
        name: 'Khu Trục Hạm Thiết Giáp Hư Không',
        hull_id: 'destroyer',
        total_hp: 2300,
        total_shield: 250,
        total_armor: 60,
        total_damage: 190,
        damage_type: 'explosive',
        range_phase: 1,
        accuracy: 80,
        evasion: 7,
        speed: 90,
        initiative: 140,
        used_capacity: 260,
        max_capacity: 320,
        used_energy: 240,
        max_energy: 280,
        unit_cost: { metal: 550, he3: 320, credits: 120 },
        unit_build_time_sec: 80,
        is_valid: true,
      },
    },
    reward: {
      metal: 3500,
      he3: 2200,
      credits: 1100,
      exp: 1500,
    },
  },

  void_armada_5: {
    id: 'void_armada_5',
    name: 'Đại Hạm Đội Thợ Săn Tinh Vân (Lv.5)',
    level: 5,
    description: 'Hạm đội quy mô lớn với hàng công thủ toàn diện và hỏa lực đa tầng.',
    slots: [
      { slot_index: 1, design_id: 'npc_pirate_skiff', ship_count: 15 },
      { slot_index: 2, design_id: 'npc_void_destroyer', ship_count: 8 },
      { slot_index: 3, design_id: 'npc_pirate_skiff', ship_count: 15 },
      { slot_index: 4, design_id: 'npc_pirate_raider', ship_count: 10 },
      { slot_index: 5, design_id: 'npc_void_destroyer', ship_count: 6 },
      { slot_index: 6, design_id: 'npc_pirate_raider', ship_count: 10 },
    ],
    designs: {
      npc_pirate_skiff: {
        blueprint_id: 'npc_pirate_skiff',
        name: 'Chiến Tàu Cướp Skiff',
        hull_id: 'corvette',
        total_hp: 400,
        total_shield: 40,
        total_armor: 12,
        total_damage: 40,
        damage_type: 'kinetic',
        range_phase: 2,
        accuracy: 75,
        evasion: 18,
        speed: 130,
        initiative: 110,
        used_capacity: 50,
        max_capacity: 70,
        used_energy: 40,
        max_energy: 60,
        unit_cost: { metal: 80, he3: 30, credits: 10 },
        unit_build_time_sec: 15,
        is_valid: true,
      },
      npc_pirate_raider: {
        blueprint_id: 'npc_pirate_raider',
        name: 'Khinh Hạm Đột Kích Raider',
        hull_id: 'frigate',
        total_hp: 1000,
        total_shield: 140,
        total_armor: 40,
        total_damage: 105,
        damage_type: 'energy',
        range_phase: 2,
        accuracy: 82,
        evasion: 12,
        speed: 110,
        initiative: 125,
        used_capacity: 120,
        max_capacity: 150,
        used_energy: 110,
        max_energy: 140,
        unit_cost: { metal: 200, he3: 110, credits: 40 },
        unit_build_time_sec: 35,
        is_valid: true,
      },
      npc_void_destroyer: {
        blueprint_id: 'npc_void_destroyer',
        name: 'Khu Trục Hạm Thiết Giáp Hư Không',
        hull_id: 'destroyer',
        total_hp: 2500,
        total_shield: 280,
        total_armor: 65,
        total_damage: 210,
        damage_type: 'explosive',
        range_phase: 1,
        accuracy: 80,
        evasion: 7,
        speed: 90,
        initiative: 140,
        used_capacity: 260,
        max_capacity: 320,
        used_energy: 240,
        max_energy: 280,
        unit_cost: { metal: 550, he3: 320, credits: 120 },
        unit_build_time_sec: 80,
        is_valid: true,
      },
    },
    reward: {
      metal: 6000,
      he3: 3800,
      credits: 2000,
      exp: 2500,
    },
  },

  rebel_dreadnought_6: {
    id: 'rebel_dreadnought_6',
    name: 'Hạm Đội Phản Loạn Thiên Lang (Lv.6)',
    level: 6,
    description: 'Hạm đội quân phản loạn vũ trang hạng nặng với tuần dương hạm bọc giáp siêu cấp.',
    slots: [
      { slot_index: 2, design_id: 'npc_void_destroyer', ship_count: 12 },
      { slot_index: 4, design_id: 'npc_pirate_raider', ship_count: 15 },
      { slot_index: 5, design_id: 'npc_void_destroyer', ship_count: 10 },
      { slot_index: 6, design_id: 'npc_pirate_raider', ship_count: 15 },
      { slot_index: 8, design_id: 'npc_void_destroyer', ship_count: 10 },
    ],
    designs: {
      npc_pirate_raider: {
        blueprint_id: 'npc_pirate_raider',
        name: 'Khinh Hạm Đột Kích Raider',
        hull_id: 'frigate',
        total_hp: 1100,
        total_shield: 160,
        total_armor: 45,
        total_damage: 115,
        damage_type: 'energy',
        range_phase: 2,
        accuracy: 82,
        evasion: 12,
        speed: 110,
        initiative: 125,
        used_capacity: 120,
        max_capacity: 150,
        used_energy: 110,
        max_energy: 140,
        unit_cost: { metal: 200, he3: 110, credits: 40 },
        unit_build_time_sec: 35,
        is_valid: true,
      },
      npc_void_destroyer: {
        blueprint_id: 'npc_void_destroyer',
        name: 'Khu Trục Hạm Thiết Giáp Hư Không',
        hull_id: 'destroyer',
        total_hp: 2800,
        total_shield: 320,
        total_armor: 75,
        total_damage: 230,
        damage_type: 'explosive',
        range_phase: 1,
        accuracy: 80,
        evasion: 7,
        speed: 90,
        initiative: 140,
        used_capacity: 260,
        max_capacity: 320,
        used_energy: 240,
        max_energy: 280,
        unit_cost: { metal: 550, he3: 320, credits: 120 },
        unit_build_time_sec: 80,
        is_valid: true,
      },
    },
    reward: {
      metal: 10000,
      he3: 6500,
      credits: 3500,
      exp: 4000,
    },
  },

  eclipse_syndicate_7: {
    id: 'eclipse_syndicate_7',
    name: 'Tập Đoàn Hắc Ám Nhật Thực (Lv.7)',
    level: 7,
    description: 'Tổ chức cướp biển khét tiếng trang bị pháo hạt tử và giáp phản xạ năng lượng.',
    slots: [
      { slot_index: 1, design_id: 'npc_void_destroyer', ship_count: 12 },
      { slot_index: 2, design_id: 'npc_void_destroyer', ship_count: 15 },
      { slot_index: 3, design_id: 'npc_void_destroyer', ship_count: 12 },
      { slot_index: 5, design_id: 'npc_pirate_raider', ship_count: 25 },
    ],
    designs: {
      npc_pirate_raider: {
        blueprint_id: 'npc_pirate_raider',
        name: 'Khinh Hạm Đột Kích Raider',
        hull_id: 'frigate',
        total_hp: 1200,
        total_shield: 180,
        total_armor: 50,
        total_damage: 130,
        damage_type: 'energy',
        range_phase: 2,
        accuracy: 82,
        evasion: 12,
        speed: 110,
        initiative: 125,
        used_capacity: 120,
        max_capacity: 150,
        used_energy: 110,
        max_energy: 140,
        unit_cost: { metal: 200, he3: 110, credits: 40 },
        unit_build_time_sec: 35,
        is_valid: true,
      },
      npc_void_destroyer: {
        blueprint_id: 'npc_void_destroyer',
        name: 'Khu Trục Hạm Thiết Giáp Hư Không',
        hull_id: 'destroyer',
        total_hp: 3100,
        total_shield: 380,
        total_armor: 85,
        total_damage: 260,
        damage_type: 'explosive',
        range_phase: 1,
        accuracy: 80,
        evasion: 7,
        speed: 90,
        initiative: 140,
        used_capacity: 260,
        max_capacity: 320,
        used_energy: 240,
        max_energy: 280,
        unit_cost: { metal: 550, he3: 320, credits: 120 },
        unit_build_time_sec: 80,
        is_valid: true,
      },
    },
    reward: {
      metal: 16000,
      he3: 11000,
      credits: 6000,
      exp: 6500,
    },
  },

  ancient_sentinel_8: {
    id: 'ancient_sentinel_8',
    name: 'Hộ Vệ Cơ Giới Cổ Đại (Lv.8)',
    level: 8,
    description: 'Hệ thống tàu chiến AI tự hành cổ đại tuần tra ngoài rìa thiên hà.',
    slots: [
      { slot_index: 2, design_id: 'npc_void_destroyer', ship_count: 20 },
      { slot_index: 4, design_id: 'npc_void_destroyer', ship_count: 15 },
      { slot_index: 5, design_id: 'npc_void_destroyer', ship_count: 20 },
      { slot_index: 6, design_id: 'npc_void_destroyer', ship_count: 15 },
      { slot_index: 8, design_id: 'npc_pirate_raider', ship_count: 30 },
    ],
    designs: {
      npc_pirate_raider: {
        blueprint_id: 'npc_pirate_raider',
        name: 'Khinh Hạm Đột Kích Raider',
        hull_id: 'frigate',
        total_hp: 1350,
        total_shield: 220,
        total_armor: 55,
        total_damage: 150,
        damage_type: 'energy',
        range_phase: 2,
        accuracy: 85,
        evasion: 12,
        speed: 110,
        initiative: 130,
        used_capacity: 120,
        max_capacity: 150,
        used_energy: 110,
        max_energy: 140,
        unit_cost: { metal: 200, he3: 110, credits: 40 },
        unit_build_time_sec: 35,
        is_valid: true,
      },
      npc_void_destroyer: {
        blueprint_id: 'npc_void_destroyer',
        name: 'Khu Trục Hạm Thiết Giáp Hư Không',
        hull_id: 'destroyer',
        total_hp: 3500,
        total_shield: 440,
        total_armor: 95,
        total_damage: 300,
        damage_type: 'explosive',
        range_phase: 1,
        accuracy: 82,
        evasion: 7,
        speed: 90,
        initiative: 145,
        used_capacity: 260,
        max_capacity: 320,
        used_energy: 240,
        max_energy: 280,
        unit_cost: { metal: 550, he3: 320, credits: 120 },
        unit_build_time_sec: 80,
        is_valid: true,
      },
    },
    reward: {
      metal: 26000,
      he3: 18000,
      credits: 9500,
      exp: 10000,
    },
  },

  nebula_behemoth_9: {
    id: 'nebula_behemoth_9',
    name: 'Trùm Cự Hạm Băng Tinh (Lv.9)',
    level: 9,
    description: 'Hạm đội thiết giáp siêu trọng với khiên đa tầng và hỏa lực pháo từ trường áp đảo.',
    slots: [
      { slot_index: 1, design_id: 'npc_void_destroyer', ship_count: 25 },
      { slot_index: 2, design_id: 'npc_void_destroyer', ship_count: 30 },
      { slot_index: 3, design_id: 'npc_void_destroyer', ship_count: 25 },
      { slot_index: 5, design_id: 'npc_void_destroyer', ship_count: 35 },
    ],
    designs: {
      npc_void_destroyer: {
        blueprint_id: 'npc_void_destroyer',
        name: 'Khu Trục Hạm Thiết Giáp Hư Không',
        hull_id: 'destroyer',
        total_hp: 4000,
        total_shield: 520,
        total_armor: 110,
        total_damage: 350,
        damage_type: 'explosive',
        range_phase: 1,
        accuracy: 85,
        evasion: 8,
        speed: 95,
        initiative: 150,
        used_capacity: 260,
        max_capacity: 320,
        used_energy: 240,
        max_energy: 280,
        unit_cost: { metal: 550, he3: 320, credits: 120 },
        unit_build_time_sec: 80,
        is_valid: true,
      },
    },
    reward: {
      metal: 42000,
      he3: 28000,
      credits: 15000,
      exp: 16000,
    },
  },

  void_overlord_10: {
    id: 'void_overlord_10',
    name: 'Bá Chủ Hư Không Vô Tận (Lv.10)',
    level: 10,
    description: 'Kỳ hạm huyền thoại của chúa tể hư không, thử thách tối thượng cho mọi liên minh đô đốc.',
    slots: [
      { slot_index: 1, design_id: 'npc_void_destroyer', ship_count: 40 },
      { slot_index: 2, design_id: 'npc_void_destroyer', ship_count: 50 },
      { slot_index: 3, design_id: 'npc_void_destroyer', ship_count: 40 },
      { slot_index: 4, design_id: 'npc_void_destroyer', ship_count: 30 },
      { slot_index: 5, design_id: 'npc_void_destroyer', ship_count: 50 },
      { slot_index: 6, design_id: 'npc_void_destroyer', ship_count: 30 },
      { slot_index: 8, design_id: 'npc_void_destroyer', ship_count: 40 },
    ],
    designs: {
      npc_void_destroyer: {
        blueprint_id: 'npc_void_destroyer',
        name: 'Khu Trục Hạm Thiết Giáp Hư Không',
        hull_id: 'destroyer',
        total_hp: 5000,
        total_shield: 650,
        total_armor: 135,
        total_damage: 420,
        damage_type: 'explosive',
        range_phase: 1,
        accuracy: 88,
        evasion: 10,
        speed: 100,
        initiative: 160,
        used_capacity: 260,
        max_capacity: 320,
        used_energy: 240,
        max_energy: 280,
        unit_cost: { metal: 550, he3: 320, credits: 120 },
        unit_build_time_sec: 80,
        is_valid: true,
      },
    },
    reward: {
      metal: 75000,
      he3: 50000,
      credits: 28000,
      exp: 30000,
    },
  },
};
